import {StateController} from 'panel';

import {reporter} from 'common/report/error-reporting';
import {CreateKpiDashboardRequest, FoundationalTemplatesRequest} from 'app_api/projects/dashboards/types';
import {Subscribable} from 'common/types/context/subscribable';

import {Store} from '../stores/store';
import {DashboardsMap} from '../stores/dashboards';
import {Dashboard} from '../dashboards/types';
import {DashboardModelController} from './dashboard-model-controller';
import {DashboardsModelClient, QueryAllDashboardType} from './types';

type DashboardSelectorControllerState<CustomSelectorClient extends DashboardsModelClient> = {
  activeDashboardController: DashboardModelController<CustomSelectorClient>;
  bookmarkDashboardIds: Set<number>;
  dashboards: Dashboard[];
  loaded: boolean;
};

export class DashboardSelectorController<CustomSelectorClient extends DashboardsModelClient = DashboardsModelClient>
  extends StateController<DashboardSelectorControllerState<CustomSelectorClient>>
  implements Subscribable {
  projectId: number;
  bookmarkID: number;
  client: CustomSelectorClient;
  workspaceId: number;
  dashboardStore: Store<DashboardsMap>;
  _loadDashboardsPromises: QueryAllDashboardType;
  _loadBookmarkDashboardsPromise: Promise<number[]>;

  constructor({
    projectId,
    bookmarkID,
    dashboardsClient,
    workspaceId,
    dashboardStore = null,
  }: {
    projectId?: number | string;
    bookmarkID?: number;
    dashboardsClient?: CustomSelectorClient;
    workspaceId?: number | string;
    dashboardStore?: Store<DashboardsMap>;
  } = {}) {
    super({});
    this.projectId = typeof projectId === `string` ? parseInt(projectId) : projectId;
    this.bookmarkID = bookmarkID;
    this.client = dashboardsClient;
    this.workspaceId = typeof workspaceId === `string` ? parseInt(workspaceId) : workspaceId;

    // note: will be null if this is a public URL, stores don't work in unauthenticated environments
    this.dashboardStore = dashboardStore;
  }
  subscribe(callback: () => void): this {
    this.subscribeUpdates(callback);
    return this;
  }
  unsubscribe(callback: () => void): this {
    this.unsubscribeUpdates(callback);
    return this;
  }

  get defaultState() {
    return {
      activeDashboardController: null,
      bookmarkDashboardIds: new Set<number>(),
      dashboards: [],
      loaded: false,
    };
  }

  _updateCachedDashboard(id, results) {
    const dashboardInList = this.state.dashboards.find((d) => d.id === id);
    if (dashboardInList) {
      Object.assign(dashboardInList, results);
      delete dashboardInList[`cards`]; // don't keep cards in list item
      this._update({dashboards: this.state.dashboards});
    } else {
      this._update({dashboards: [...this.state.dashboards, {...results, id}]});
    }
  }

  getDashboards() {
    return this.state.dashboards;
  }

  bookmarkContainsDashboardId(dashboardID) {
    return this.state.bookmarkDashboardIds.has(dashboardID);
  }

  getLoaded() {
    return this.state.loaded;
  }

  // redundant method that is more consistent with out app verbiage
  isLoading() {
    return !this.state.loaded;
  }

  getActiveDashboardController() {
    return this.state.activeDashboardController;
  }

  setBookmarkID(bookmarkID) {
    this.bookmarkID = bookmarkID;
    this._update({loaded: false});
  }

  createDashboardModelController(dashboardID) {
    const dashboardInList = this.state.dashboards.find((d) => d.id === dashboardID);
    return new DashboardModelController({
      model: dashboardInList || {id: dashboardID},
      selectorController: this,
      dashboardsClient: this.client,
      dashboardStore: this.dashboardStore,
    });
  }

  async loadDashboards() {
    this._update({loaded: false});
    if (this.bookmarkID) {
      // prevent redundant network requests
      if (!this._loadDashboardsPromises || !this._loadBookmarkDashboardsPromise) {
        this._loadDashboardsPromises = this.client.queryAll(
          {projectId: this.projectId, workspaceId: this.workspaceId},
          {clearCache: true},
        );
        this._loadBookmarkDashboardsPromise = this.client.queryBookmarkDashboardIds(
          {projectId: this.projectId, bookmarkID: this.bookmarkID, workspaceId: this.workspaceId},
          {clearCache: true},
        );
      }
      // load dashboards containing specified bookmark
      try {
        // note: we store promises separately then "await Promise.all([])" here because
        // array-deconstructing a stored Promise.all([]) promise directly appears to fail when transpiled by babel
        const [dashboards, bookmarkDashboardIds] = await Promise.all([
          this._loadDashboardsPromises.network,
          this._loadBookmarkDashboardsPromise,
        ]);
        this._update({
          dashboards,
          bookmarkDashboardIds: new Set(bookmarkDashboardIds),
          loaded: true,
        });
      } finally {
        this._loadDashboardsPromises = null;
        this._loadBookmarkDashboardsPromise = null;
      }
    } else {
      // prevent redundant network requests
      if (!this._loadDashboardsPromises) {
        this._loadDashboardsPromises = this.client.queryAll(
          {projectId: this.projectId, workspaceId: this.workspaceId},
          {clearCache: true},
        );
      }
      Object.values(this._loadDashboardsPromises).forEach((promise) =>
        promise.then((dashboards) => {
          dashboards && this._update({dashboards, loaded: true});
        }),
      );

      // fallback to network if cached value doesn't exist
      (await this._loadDashboardsPromises.cached) || (await this._loadDashboardsPromises.network);
      Promise.all(Object.values(this._loadDashboardsPromises)).finally(() => (this._loadDashboardsPromises = null));
    }
    return this.state.dashboards;
  }

  async createDashboard({title = null, fields = {}}: {title?; fields?: TODO} = {}) {
    if (!title && !fields.duplicate) {
      throw new Error(`Both title or duplicate cannot be empty`);
    } else if (title && fields.duplicate) {
      throw new Error(`Only title or duplicate can be specified`);
    }

    const results = await this.client.postCreateDashboard({
      projectId: this.projectId,
      fields: fields.duplicate ? fields : {...fields, title},
      workspaceId: this.workspaceId,
    });
    this._update({dashboards: [...this.state.dashboards, results]});
    return results;
  }

  async createRCADashboard(alertId, timestamp = null) {
    const results = await this.client.postCreateRCADashboard({
      projectId: this.projectId,
      alertId,
      timestamp,
      workspaceId: this.workspaceId,
    });
    this._update({dashboards: [...this.state.dashboards, results]});
    return results;
  }

  // for template types, check TemplateTypes enum
  // TODO @JakeW: Remove after additional templates launch
  async createTemplateDashboard({payload}: {payload?: CreateKpiDashboardRequest} = {}) {
    const results = await this.client.postCreateTemplateDashboard({
      projectId: this.projectId,
      mappedEvents: payload,
      workspaceId: this.workspaceId,
    });

    // TODO: this is legacy code. We were checking this even when the types doesn't match
    // according to the ts types, `status` is a number and `statusCode` doesn't exist
    if ((results as any)?.status === `error` || (results as any)?.statusCode >= 400) {
      return results;
    } else {
      this._update({dashboards: [...this.state.dashboards, results as Dashboard]});
      return results;
    }
  }

  async createFoundationalTemplateDashboard(payload: FoundationalTemplatesRequest) {
    const results = await this.client.postCreateFoundationalTemplateDashboard({
      projectId: this.projectId,
      fields: payload.fields,
      templateType: payload.template_type,
      workspaceId: this.workspaceId,
    });

    // TODO: this is legacy code. We were checking this even when the types doesn't match
    // according to the ts types, `status` is a number and `statusCode` doesn't exist
    if ((results as any)?.status === `error` || (results as any)?.statusCode >= 400) {
      return results;
    } else {
      this._update({dashboards: [...this.state.dashboards, results as Dashboard]});
      return results;
    }
  }

  async deleteDashboard(dashboardID) {
    if (!dashboardID) {
      throw new Error(`dashboardID must be specified`);
    }

    const {dashboards} = this.state;
    try {
      this._update({dashboards: dashboards.filter((dash) => dash.id !== dashboardID)});

      await this.client.deleteDashboard({
        dashboardID,
        projectId: this.projectId,
        workspaceId: this.workspaceId,
      });
    } catch (error) {
      if (error.name !== `DashboardDoesNotExistError`) {
        this._update({dashboards});
      }
      throw error;
    }
  }

  async changeActiveDashboard(dashboardID) {
    const activeDashboardController = this.createDashboardModelController(dashboardID);
    this._update({activeDashboardController});
    try {
      await activeDashboardController.loadModel();
    } catch (err) {
      reporter.error(err);
    }
  }

  resetActiveDashboard() {
    this.setBookmarkID(null);
    this._update({activeDashboardController: this.defaultState.activeDashboardController});
  }
}
